import { customSortCaret } from 'helpers/bootstrap_table_helper'
import { forColumnHeaderWrapper } from 'helpers/sf_utils'
import LoadingDashboardTableSkeleton from 'pages/Dashboard/LoadingDashboardTableSkeleton'
import { UI_PERMISSION_KEY } from 'pages/Roles/newFeaturesAndPermissionsData'
import React, { useState } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit'
import OffsetPagination from 'components/Common/OffsetPagination'
import { useSelector } from 'react-redux'
import { Card, CardBody, Col, Form, Row, TabPane, UncontrolledTooltip } from 'reactstrap'
import { dateCreatedFormatter, defaultFormatter } from '../Users/UserComponents/UserList/Formatters'
import { RULE_STATUS } from 'helpers/sf_constants'
import RulesLogModal from './RulesLogModal'
import useRemoteTableSort from 'hooks/useRemoteTableSort'

const RulesDashboardTable = ({ fetchRuleLogsPage, onRuleLogsSortChange, ruleLogsSort }) => {

  const {
    current_user,
    current_role_permissions_state,
    rule_logs,
    rule_logs_loading,
    rule_logs_last_page_fetched,
    rule_logs_has_next_page,
    rule_logs_total_number_of_pages,
    rule_logs_has_lookahead_pages,
    rule_logs_lookahead_pages,
  } = useSelector((state) => ({
    current_user: state.LoginAdminUser.current_user,
    current_role_permissions_state: state.Role.current_role_permissions_state,
    rule_logs_last_page_fetched: state.DevDashboard.rule_logs_last_page_fetched,
    rule_logs_has_next_page: state.DevDashboard.rule_logs_has_next_page,
    rule_logs_total_number_of_pages: state.DevDashboard.rule_logs_total_number_of_pages,
    rule_logs_has_lookahead_pages: state.DevDashboard.rule_logs_has_lookahead_pages,
    rule_logs_lookahead_pages: state.DevDashboard.rule_logs_lookahead_pages,
    rule_logs: state.DevDashboard.rule_logs,
    rule_logs_loading: state.DevDashboard.rule_logs_loading,
  }));

  const [rulesLogModalState, setRulesLogModalState] = useState({ active: false, row: null });

  const { ExportCSVButton } = CSVExport;

  const apiColumns = [
    {
      dataField: 'id',
      text: 'ID',
      headerFormatter: forColumnHeaderWrapper,
      formatter: (cellContent, row) => {
        return (
          <div
            className='text-sfblue'
            style={{ cursor: 'pointer' }}
            onClick={() => handleOnclickView(row)}
          >
            {cellContent}
          </div>
        )
      },
      sort: true,
    },
    {
      dataField: 'timestamp',
      text: 'Timestamp',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: dateCreatedFormatter
    },
    {
      dataField: 'company_name',
      text: 'Account Name',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
    },
    {
      dataField: 'product_name',
      text: 'Product Type',
      headerFormatter: forColumnHeaderWrapper,
      formatter: defaultFormatter,
      sort: true,
    },
    {
      dataField: 'rule_name',
      text: 'Rule Name',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
    },
    {
      dataField: 'status',
      text: 'Status',
      headerFormatter: forColumnHeaderWrapper,
      sort: true,
      formatter: (cellContent, row) => {
        let iconClassName = cellContent === RULE_STATUS.PASSED ? 'bx bx-info-circle text-success me-2' : 'bx bx-info-circle text-danger me-2';
        let textClassName = cellContent === RULE_STATUS.PASSED ? 'fsnormal text-success' : 'fsnormal text-danger';
        let id = cellContent === RULE_STATUS.PASSED ? `${RULE_STATUS.PASSED}${row?.id}` : `${RULE_STATUS.FAILED}${row?.id}`;

        return (
          <>
            <i className={iconClassName} />
            <span className={textClassName} id={id}>{cellContent === RULE_STATUS.PASSED ? 'Passed' : 'Failed'}</span>
            <UncontrolledTooltip placement="left" target={id}>
              Description: {row?.rule_desc}
            </UncontrolledTooltip>
          </>
        );
      }
    },
  ]

  function handleOnclickView(row) {
    setRulesLogModalState({ active: true, row: row })
  }

  function onClickCloseModal() {
    setRulesLogModalState({ active: false, row: null })
  }

  const remoteSortProps = useRemoteTableSort(
    ruleLogsSort?.sort_column || 'timestamp',
    ruleLogsSort?.sort_direction || 'desc',
    onRuleLogsSortChange,
  )

  return (
    <React.Fragment>
      <RulesLogModal
        show={rulesLogModalState}
        onClickCloseModal={onClickCloseModal}
      />
      <Row>
        <Col>
          {rule_logs_loading && !rule_logs?.length ? <LoadingDashboardTableSkeleton rows={6} cols={apiColumns.length} /> : (
            <>
              <TabPane tabId="1">
                <Row>
                  <Col lg="12">
                    <Card>
                      <CardBody>
                        <ToolkitProvider
                          keyField='id'
                          columns={apiColumns}
                          data={rule_logs}
                          exportCSV={{
                            fileName: "DMSLogs.csv",
                            exportAll: false,
                          }}
                        >
                          {
                            toolkitProps => (
                              <React.Fragment>
                                <Form>
                                  <Col xl="auto" className="ms-auto align-items-end">
                                    <div className="mb-2">
                                      <div>
                                        {current_role_permissions_state && current_role_permissions_state.find(single_obj => single_obj.ui_key === UI_PERMISSION_KEY.EXPORT_DATA).isActive && (
                                          <ExportCSVButton className="btn btn-outline-sfblue" {...toolkitProps.csvProps}>
                                            <i className="bx bx-import font-size-14 align-middle me-2" />
                                            Export
                                          </ExportCSVButton>
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                </Form>
                                <Row className="sfTable-Container" style={{ zIndex: 1 }}>
                                  <Col xl="12">
                                    {!rule_logs?.length > 0 && rule_logs_loading ? (
                                      <LoadingDashboardTableSkeleton rows={6} cols={apiColumns?.length} />
                                    ) : (
                                      <BootstrapTable
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        striped={false}
                                        defaultSorted={remoteSortProps.defaultSorted}
                                        remote={remoteSortProps.remote}
                                        onTableChange={remoteSortProps.onTableChange}
                                        rowClasses="tr-hover"
                                        headerClasses="sftableHeader thead-h50 text-black"
                                        classes="table align-middle table-nowrap text-black"
                                        headerWrapperClasses={"thead-light"}
                                        noDataIndication="You currently don't have any data"
                                        sort={{ sortCaret: customSortCaret }}
                                        {...toolkitProps.baseProps}
                                      />
                                    )}
                                  </Col>
                                </Row>
                                <Row>
                                  <Col xl='auto' className='ms-auto'>
                                    <OffsetPagination
                                      currentPage={rule_logs_last_page_fetched}
                                      totalPages={rule_logs_total_number_of_pages}
                                      hasNextPage={rule_logs_has_next_page}
                                      hasDeepOffsetPages={rule_logs_has_lookahead_pages}
                                      offsetLookAheadPages={rule_logs_lookahead_pages}
                                      loading={rule_logs_loading}
                                      onPageChange={fetchRuleLogsPage}
                                      className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                    />
                                  </Col>
                                </Row>
                              </React.Fragment>
                            )
                          }
                        </ToolkitProvider>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>
            </>
          )}
        </Col>
      </Row >
    </React.Fragment>
  )
}

export default RulesDashboardTable
